import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isAndroidORIosApp } from 'webtonative';
import { socialLogin } from 'webtonative/SocialLogin';
import * as nativeOneSignal from 'webtonative/OneSignal';
import { authAPI, getStoredToken, setStoredToken, setStoredRefreshToken, getStoredRefreshToken } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // iter128 — Use the `api` axios instance (via authAPI.getMe) so the 401
    // → /auth/refresh interceptor gets a chance to silently mint a new
    // session before we conclude "logged out". Previously this used a raw
    // axios call which bypassed the interceptor entirely — the direct cause
    // of the iOS persistent-login regression: an expired session at cold
    // start dumped the user to logged-out even though a valid refresh token
    // was sitting in storage.
    //
    // Belt-and-suspenders: if getMe still fails (e.g. no session at all),
    // AND we have a refresh token, explicitly try the refresh endpoint once
    // more from the raw axios call so we can rehydrate the storage before
    // giving up. This second attempt also helps native shells whose
    // WKWebView data-store may have been re-created between launches.
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch (_) {
      const rt = getStoredRefreshToken();
      if (rt) {
        try {
          const r = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
            { refresh_token: rt },
            { withCredentials: true, timeout: 8000 },
          );
          const newSess = r?.data?.session_token;
          const newRefresh = r?.data?.refresh_token;
          if (newSess) setStoredToken(newSess);
          if (newRefresh) setStoredRefreshToken(newRefresh);
          if (newSess) {
            const meRes = await authAPI.getMe();
            setUser(meRes.data);
            setLoading(false);
            return;
          }
        } catch (_refreshErr) {
          // Refresh failed — nuke it so we don't loop next reload.
          setStoredRefreshToken('');
        }
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      const token = getStoredToken();
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // Inside the WebToNative app, also sign the user out of Google natively so
    // the next "Continue with Google" tap re-shows the account picker instead
    // of silently re-authenticating into the same account. No-op on the web.
    try {
      if (isAndroidORIosApp) {
        socialLogin.google.logout({ callback: () => {} });
        // iter130 — Apple parity. Some WebToNative SDK builds expose
        // `socialLogin.apple.logout`; when it exists, clear the cached
        // Apple credential so the next Apple sign-in doesn't silently
        // reuse the previous authorization on the same device.
        try {
          if (socialLogin.apple && typeof socialLogin.apple.logout === 'function') {
            socialLogin.apple.logout({ callback: () => {} });
          }
        } catch (_e) { /* best-effort */ }
        // Also clear the OneSignal external_id on the native SDK so the
        // user stops receiving pushes for the account they just signed
        // out of. The OneSignalProvider effect will also do this when
        // `user` becomes null, but doing it here is more reliable.
        try { nativeOneSignal.removeExternalUserId(); } catch (_e) {}
      }
    } catch (_e) { /* best-effort; never block logout */ }
    setStoredToken('');
    setStoredRefreshToken('');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};